import React from 'react';
import type { LucideIcon } from 'lucide-react';

import { EmptyState } from './EmptyState';
import { Button } from './ui/button';

export interface ListEmptyStateProps {
  icon?: LucideIcon;
  /** Plural glossary noun, the same as the DataTable's `itemNoun`: 'sensors', 'event types'. */
  noun: string;
  /** The APPLIED search (`query.search`), '' when none. */
  search: string;
  /** `query.clearSearch`. */
  onClearSearch: () => void;
  /** `query.hasActiveFilters` — filters only, never search. */
  filterActive?: boolean;
  /** Names the filtered set in the default filtered title: 'No {filterLabel}' ('No draft templates'). */
  filterLabel?: string;
  /** `query.clearFilters` — restores every filter's default AND clears the search. */
  onClearFilters?: () => void;
  /** Label for the default filtered-mode reset. */
  clearFiltersLabel?: string;
  /** Filtered-mode overrides (no search, a non-default filter). */
  filtered?: {
    icon?: LucideIcon;
    title?: string;
    description?: string;
    /**
     * `undefined` → the default 'Clear filters' button. A node → a named reset
     * ("Show my organization's sensors"). `null` → no reset, because the empty
     * result is itself the answer ("Nothing waiting for review").
     */
    action?: React.ReactNode | null;
  };
  /** Nothing active: teach the concept; the page gates its create CTA. */
  firstRun: { icon?: LucideIcon; title: string; description?: string; action?: React.ReactNode };
  className?: string;
}

/**
 * The one empty state for a list (DESIGN_SYSTEM.md §5 Empty states). It picks
 * the mode, in this order, so a page cannot get the precedence wrong:
 *
 * 1. **No results** — a search is active, with or without a filter. The copy is
 *    written HERE and nowhere else: No {noun} match "{search}" (straight quotes),
 *    'Try a different search.' + 'Clear search', or, with a filter also active,
 *    'Try a different search or filter.' + 'Clear filters' (which clears both).
 * 2. **Filtered** — no search, a non-default filter. Says the filtered set is
 *    empty and offers a reset; never first-run copy or a create button.
 * 3. **First run** — nothing active: the page's teaching copy and gated CTA.
 *
 * EmptyState stays presentational; this helper only chooses what it shows.
 */
export const ListEmptyState: React.FC<ListEmptyStateProps> = ({
  icon,
  noun,
  search,
  onClearSearch,
  filterActive = false,
  filterLabel,
  onClearFilters,
  clearFiltersLabel = 'Clear filters',
  filtered,
  firstRun,
  className,
}) => {
  const query = search.trim();

  if (query) {
    const clearsFilters = filterActive && Boolean(onClearFilters);
    return (
      <EmptyState
        icon={icon}
        className={className}
        title={`No ${noun} match "${query}"`}
        description={filterActive ? 'Try a different search or filter.' : 'Try a different search.'}
        action={
          clearsFilters ? (
            <Button variant="outline" onClick={onClearFilters}>
              Clear filters
            </Button>
          ) : (
            <Button variant="outline" onClick={onClearSearch}>
              Clear search
            </Button>
          )
        }
      />
    );
  }

  if (filterActive) {
    if (import.meta.env.DEV && !onClearFilters && filtered?.action === undefined) {
      console.error(
        'ListEmptyState: a filtered empty state needs onClearFilters, a named reset, or action: null (DESIGN_SYSTEM.md §5).',
      );
    }
    const action =
      filtered?.action === null
        ? undefined
        : filtered?.action !== undefined
          ? filtered.action
          : onClearFilters
            ? (
              <Button variant="outline" onClick={onClearFilters}>
                {clearFiltersLabel}
              </Button>
            )
            : undefined;
    return (
      <EmptyState
        icon={filtered?.icon ?? icon}
        className={className}
        title={filtered?.title ?? `No ${filterLabel ?? noun}`}
        description={filtered?.description}
        action={action}
      />
    );
  }

  return (
    <EmptyState
      icon={firstRun.icon ?? icon}
      className={className}
      title={firstRun.title}
      description={firstRun.description}
      action={firstRun.action}
    />
  );
};
